import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { motion } from "framer-motion";

interface PrivacyPolicyProps {
  onBack: () => void;
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onBack }) => {
  return (
    <Box sx={{ py: 4, pb: 8, minHeight: "100vh" }}>
      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Button
            startIcon={<ArrowBack />}
            onClick={onBack}
            sx={{ mb: 3 }}
            variant="outlined"
          >
            Back to Home
          </Button>

          <Paper sx={{ p: 4, borderRadius: 2 }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{ mb: 3, fontWeight: 700 }}
            >
              Privacy Policy
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Last updated: {new Date().toLocaleDateString()}
            </Typography>

            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
              At Dad2Kid, we respect your privacy and are committed to
              protecting your personal data. This privacy policy explains how we
              collect, use, and safeguard your information when you use our
              service.
            </Typography>

            <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
              1. Information We Collect
            </Typography>

            <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
              Personal Information
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="• Name and email address when you create an account"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Family information (children's ages, interests, location)"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Activity preferences and usage data"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
            </List>

            <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
              Technical Information
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="• Device information and IP address"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Browser type and version"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Usage analytics and performance data"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              2. How We Use Your Information
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
              We use your information to:
            </Typography>

            <List dense>
              <ListItem>
                <ListItemText
                  primary="• Provide personalized activity recommendations"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Improve our service and user experience"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Send you relevant updates and newsletters (with your consent)"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Ensure security and prevent fraud"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              3. Data Sharing and Third Parties
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
              We do not sell, trade, or rent your personal information to third
              parties. We may share data with:
            </Typography>

            <List dense>
              <ListItem>
                <ListItemText
                  primary="• Service providers who help us operate our platform"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Analytics services to improve our service"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Legal authorities when required by law"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              4. Your Rights (GDPR/CCPA)
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
              You have the right to:
            </Typography>

            <List dense>
              <ListItem>
                <ListItemText
                  primary="• Access your personal data"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Correct inaccurate data"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Request deletion of your data"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Opt-out of marketing communications"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Export your data in a portable format"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              5. Data Security
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
              We implement appropriate security measures to protect your
              personal information:
            </Typography>

            <List dense>
              <ListItem>
                <ListItemText
                  primary="• Encryption of data in transit and at rest"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Regular security assessments"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Access controls and authentication"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              6. Cookies and Tracking
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
              We use cookies and similar technologies to:
            </Typography>

            <List dense>
              <ListItem>
                <ListItemText
                  primary="• Remember your preferences"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Analyze website usage"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Improve our service"
                  sx={{ "& .MuiListItemText-primary": { fontSize: "0.95rem" } }}
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              7. Contact Us
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
              If you have any questions about this Privacy Policy or our data
              practices, please contact us:
            </Typography>

            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Email:</strong> privacy@dad2kid.com
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Address:</strong> 123 Family Street, San Francisco, CA
                94102
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Phone:</strong> +1 (555) 123-4567
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
              This privacy policy is effective as of the date listed above and
              will remain in effect except with respect to any changes in its
              provisions in the future.
            </Typography>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
